import type { ReactNode } from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import { Button } from "../ui/button/index.js";
import { IsolatedBrands } from "./IsolatedBrands.js";
import { cn } from "../../lib/utils.js";

export type AppNoticeSeverity = "info" | "success" | "warning" | "error";

const SEVERITY_CLASS: Record<AppNoticeSeverity, string> = {
  info: "border-line bg-surface-muted text-ink",
  success: "border-success/25 bg-success-muted/70 text-ink",
  warning: "border-warning/30 bg-warning-muted/70 text-ink",
  error: "border-error/25 bg-error-muted/70 text-ink",
};

const ICON_CLASS: Record<AppNoticeSeverity, string> = {
  info: "text-accent",
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
};

function NoticeIcon({ severity }: { severity: AppNoticeSeverity }): React.JSX.Element {
  const className = cn("size-4 shrink-0", ICON_CLASS[severity]);
  switch (severity) {
    case "info":
      return <Info className={className} aria-hidden="true" />;
    case "success":
      return <CircleCheck className={className} aria-hidden="true" />;
    case "warning":
      return <TriangleAlert className={className} aria-hidden="true" />;
    case "error":
      return <CircleAlert className={className} aria-hidden="true" />;
    default: {
      const _exhaustive: never = severity;
      return _exhaustive;
    }
  }
}

export function AppNotice({
  severity,
  title,
  description,
  action,
  onDismiss,
  dismissLabel,
  testId,
  className,
}: {
  severity: AppNoticeSeverity;
  title?: string;
  description?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  testId?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      role={severity === "error" || severity === "warning" ? "alert" : "status"}
      data-testid={testId}
      data-notice-severity={severity}
      className={cn(
        "flex w-full items-start gap-2.5 rounded-lg border px-3.5 py-3 text-start",
        SEVERITY_CLASS[severity],
        className,
      )}
    >
      <NoticeIcon severity={severity} />
      <div className="grid min-w-0 flex-1 gap-1">
        {title ? (
          <p className="m-0 text-[0.8125rem] font-semibold text-foreground">
            <IsolatedBrands text={title} />
          </p>
        ) : null}
        {description ? (
          <div className="text-[0.8125rem] leading-snug text-muted">
            {typeof description === "string" ? <IsolatedBrands text={description} /> : description}
          </div>
        ) : null}
        {action || (onDismiss && dismissLabel) ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {action}
            {onDismiss && dismissLabel ? (
              <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
                {dismissLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
